/**
 * Der Abschnitt „DSV-Sendung" (`ship-sendung`, erster Abschnitt des Reiters „Spedition").
 *
 * Ohne verknuepfte Sendung (`sendung === null`) steht hier das Suchfeld
 * `components/Shipments/SendungsSuche.tsx` (siehe dessen Kopfkommentar zur generischen Umstellung
 * auf `SendungsTreffer`). Eine Auswahl dort ruft direkt `onVerknuepfen(treffer.id)` - OHNE
 * Fremd-Check, siehe Abschnitt „Konfliktdialog" unten.
 *
 * Mit verknuepfter Sendung stehen stattdessen deren Kopfdaten als reine Anzeigefelder (gesperrte
 * Eingabefelder, `readOnly aria-readonly` wie „vsl. LT 74722 Buchen" in `Transport.tsx`) plus ein
 * Knopf „Verknüpfung lösen", der `onLoesen(sendung.id)` ruft.
 *
 * `laeuft`: solange true (ein Verknuepfen/Loesen-Rundlauf ist unterwegs), ist der GANZE
 * Verknuepfungsbereich gesperrt - mit Sendung der Loesen-Knopf, ohne Sendung das Suchfeld
 * (`SendungsSuche`-Prop `disabled`, C2 einer Pruefung, 2026-08-21). Ohne die zweite Haelfte liesse
 * ein zweiter Klick in der Trefferliste `onVerknuepfen` erneut auflaufen, waehrend der erste
 * Rundlauf noch unterwegs ist - genau das verhindert `disabled` jetzt zusaetzlich zum
 * `SendungsSuche.tsx`-eigenen Stale-Guard (der schuetzt nur vor VERALTETEN Antworten, nicht vor
 * einer zweiten laufenden Anfrage).
 *
 * ================= Konfliktdialog beim Verknuepfen - ENTFERNT (Sammelsendung, Spec 2026-09-07)
 * =================
 *
 * Bis hierher stand: waehlt ein Mitarbeiter im Suchfeld eine Sendung, die bereits an einer FREMDEN
 * Bestellung haengt (Order-ID ungleich der des offenen Drawers), erscheint vor dem Verknuepfen ein
 * Bestaetigungsdialog ("Dort lösen und hier verknüpfen" / "Bei beiden lassen" / "Abbrechen") statt
 * sofort zu verknuepfen - Auftrag „Sendungs-Dropdown, Portal-Link, Konfliktdialog" (2026-08-21, Ziel
 * 3), begruendet mit "das Datenmodell bleibt n:m, es wird GEWARNT, nicht verboten".
 *
 * Das ist mit der Sammelsendung (Migration 039, `ux_order_shipments_order`) GENAU DER ERWUENSCHTE
 * NORMALFALL geworden, keine Warnung mehr wert: eine Sendung SOLL mehrere Bestellungen tragen. Der
 * serverseitige Hebel dahinter (`bestehendeLoesen`, trennte fremde Bestellungen von der
 * Zielsendung - Kopfkommentar `backend/src/http/routes/orderShipments.ts`) ist ersatzlos entfallen;
 * "Dort lösen und hier verknüpfen" liesse sich technisch nicht mehr abbilden. `beiAuswahl` unten
 * verknuepft deshalb wieder immer direkt, wie vor Auftrag „...Konfliktdialog".
 *
 * Die eigentliche NEUE Rueckfrage dieses Vorhabens ("diese Bestellung hängt an Sendung Y -
 * umhängen?") betrifft einen ANDEREN Fall - die ANFRAGENDE Bestellung haengt bereits an einer
 * ANDEREN Sendung als der gewaehlten - und lebt bewusst NICHT hier, sondern eine Ebene hoeher in
 * `App.tsx#speditionVerknuepfen`: nur dort ist der 409 (`KonfliktFehler`, `api/basis.ts`) sichtbar,
 * den die Route bei diesem Fall liefert. Diese Komponente reicht dafuer weiterhin nur EIN Argument
 * durch `onVerknuepfen` - siehe dessen Typ unten.
 *
 * ================= Zuordnungsvorschlag (Auftrag 2b, "Uebernehmen", 25.08.2026) =================
 *
 * Im Leerzustand (`!sendung`) steht jetzt, VOR dem Suchfeld, ein Vorschlagsblock, sofern
 * `App.tsx` einen `vorschlag` (`GET .../shipment/vorschlag`) geladen hat - genau dann, wenn GENAU
 * eine DSV-Sendung die Bestellnummer dieser Bestellung nennt (siehe
 * `backend/src/dsv/sendungsVorschlag.ts`). Muster wie `App/KonfliktBanner.tsx`: zwei
 * Bedienelemente, "Übernehmen" ruft DENSELBEN `onVerknuepfen`-Weg wie eine Auswahl aus der Suche -
 * kein zweiter Schreibweg. "Verwerfen" blendet den Block NUR LOKAL aus (kein Server-Ruecklauf, kein
 * `bestaetigt`-Feld - der naechste Reiterwechsel zeigt ihn bei Bedarf wieder).
 *
 * ANDERS als eine Auswahl aus der Suche laeuft "Übernehmen" NICHT durch den Konfliktdialog: die
 * schlanke `SendungsVorschlag`-Form traegt kein `verknuepfteBestellungen` (Auftrag 2b begrenzt die
 * Antwort bewusst auf das, was der Nutzer zum ERKENNEN braucht, siehe dortiger Backend-Docstring) -
 * ein Fremd-Check liesse sich ohne diese Daten nicht seriös durchfuehren. Das ist keine Luecke,
 * sondern der Auftrag woertlich: "'Übernehmen' ruft den vorhandenen speditionVerknuepfen()-Weg mit
 * der vorgeschlagenen shipmentId."
 *
 * `vorschlagVerworfen` setzt bei einem Wechsel der Bestellung ODER des vorgeschlagenen Treffers
 * selbst zurueck (Effekt unten) - sonst bliebe ein einmal weggeklickter Vorschlag beim naechsten
 * Oeffnen einer ANDEREN Bestellung faelschlich unterdrueckt (derselbe State-Baustein ueberlebt
 * einen Reiterwechsel innerhalb DESSELBEN Drawers nicht, weil `SpeditionTab`/`DsvSendung` beim
 * Reiterwechsel neu gemountet werden - der Effekt sichert nur den Fall ab, dass `vorschlag` sich
 * AENDERT, waehrend die Komponente gemountet bleibt, z. B. nach einem Nachladen).
 */
import { useEffect, useState } from "react";
import { fmtDT } from "../../../lib/format.ts";
import { fmtDateNum } from "../../../lib/datumsAnzeige.ts";
import type { SendungsTreffer, SendungsVorschlag } from "../../../lib/types.ts";
import { SendungsSuche } from "../../Shipments/SendungsSuche.tsx";
import { Ikone } from "../../Ikone.tsx";
import { feldZustandClass } from "../../../utils/feldZustand.ts";
import { TrennenDialogLazy } from "../TrennenDialogLazy.tsx";

/**
 * Rueckfall fuer `sendungsSuche` (C3 einer Pruefung, 2026-08-21): als MODULKONSTANTE statt als
 * Inline-Ausdruck im JSX unten. `SendungsSuche.tsx#gedrosselteSuche` baut ihre Drosselung mit
 * `useMemo(() => jtlSuche({ abruf: suche }), [suche])` auf der Funktionsreferenz von `suche` auf -
 * `sendungsSuche ?? (() => Promise.resolve([]))` erzeugte bei JEDEM Rendern eine NEUE Referenz, das
 * `useMemo` griff also nie, Cache und Single-Flight-Schutz waren bei jedem Tastendruck weg.
 * (G9, Auflagen-Pruefung: Verweis auf den Namen statt einer Zeilennummer - `SendungsSuche.tsx` stand
 * hier zuletzt bei Zeile 92, tatsaechlich bei 93; der Name uebersteht die naechste Verschiebung.)
 */
const KEINE_SENDUNGSSUCHE = (): Promise<SendungsTreffer[]> => Promise.resolve([]);

export interface DsvSendungProps {
  sendung: SendungsTreffer | null;
  /**
   * Der Zuordnungsvorschlag (Auftrag 2b, 25.08.2026), oder `null`/`undefined` - nur relevant, wenn
   * `sendung === null` (siehe Kopfkommentar "Zuordnungsvorschlag"). `App.tsx` laedt ihn ueber
   * `sendungsVorschlagLaden()`; ein Fehlschlag dort bleibt dort ein stiller Normalfall, hier kommt
   * dann schlicht kein Wert an.
   */
  vorschlag?: SendungsVorschlag | null;
  sendungsSuche?: (begriff: string) => Promise<SendungsTreffer[]>;
  /**
   * Verknuepft. NUR EIN Argument (Sammelsendung, Spec 2026-09-07) - der frueher zweite,
   * `bestehendeLoesen` genannte Parameter ist mit dem Konfliktdialog entfallen (siehe Kopfkommentar).
   * Ein `umhaengen`-Retry existiert zwar weiterhin, ist aber ausschliesslich internes Detail von
   * `App.tsx#speditionVerknuepfen`, nicht dieser Komponente.
   */
  onVerknuepfen?: (shipmentId: string) => void;
  onLoesen?: (shipmentId: string) => void;
  laeuft?: boolean;
  /** Fuer den Vorschlag-Verworfen-Reset (Kopfkommentar „Zuordnungsvorschlag") beim Bestellungswechsel. */
  bestellungId?: string;
  /**
   * Zahl der Bestellungen an dieser Sendung (Sammelsendung ab 2). Bestimmt die Fassung der
   * Rueckfrage vor „Verknüpfung lösen": ab 2 „herausnehmen" mit Folgen, sonst die kurze Frage.
   */
  mitgliederAnzahl?: number;
  /** Bestellnummer dieser Bestellung fuer die Rueckfrage (Fassung „sammel"). */
  bestellNr?: string;
}

interface AnzeigefeldProps {
  label: string;
  wert: string;
}

/**
 * Feldzustand-Optik (Referenz `feldzustand-spec.md`): alle acht Anzeigefelder dieser Komponente
 * sind IMMER strukturell gesperrt (hardcodiert hier, nicht bedingt) - eine Aenderung an DIESER
 * Stelle reicht, weil `Anzeigefeld` an allen acht Aufrufstellen unten generisch verwendet wird.
 */
const DSV_ANZEIGEFELD_TITEL = "Daten aus der verknüpften DSV-Sendung, hier nicht änderbar.";

function Anzeigefeld({ label, wert }: AnzeigefeldProps) {
  return (
    <div className="field">
      <label>{label}</label>
      {/*
       * Auftrag 8 (strukturell statt Sibling-Element): kein `.feld-lock-anchor`-Span mehr - das
       * Schloss-Symbol kommt seither als CSS `background-image` direkt ueber `.feld-gesperrt`
       * (`styles/neubau/feldzustand.css`). Eine Aenderung an DIESER Stelle reicht weiterhin fuer
       * alle acht Aufrufstellen unten.
       */}
      <input
        className={`mono ${feldZustandClass({ gesperrt: true })}`}
        type="text"
        value={wert}
        readOnly
        aria-readonly="true"
        title={DSV_ANZEIGEFELD_TITEL}
      />
    </div>
  );
}

/**
 * Der Satz unter dem Vorschlagsblock (Auftrag 2b) - Befund "Vier Kleinigkeiten", Punkt 2 der
 * Pruefung vom 25.08.2026: bei einer SAMMELREFERENZ (`grund` enthaelt "/") war der bisherige
 * Singular-Satz irrefuehrend ("die Bestellnummer 10187/10207/10210/10245" bei VIER Nummern) - genau
 * im Moment, in dem der Nutzer entscheidet, ob er uebernimmt. Macht jetzt BEIDES explizit: dass die
 * Sendung MEHRERE Bestellungen traegt, UND welche davon DIESE hier ist (`einzelnummer`) - der
 * Rohwert (`grund`) bleibt dabei vollstaendig sichtbar, wie vom Befund verlangt. Bei einer
 * EINZELNEN Nummer (kein "/") bleibt der urspruengliche, einfachere Satz.
 */
function vorschlagGrundText(vorschlag: SendungsVorschlag): string {
  if (!vorschlag.grund.includes("/")) {
    return `DSV nennt zu dieser Sendung die Bestellnummer ${vorschlag.grund}`;
  }
  return (
    `DSV nennt zu dieser Sendung mehrere Bestellnummern (${vorschlag.grund}) - ` +
    `diese Bestellung ist ${vorschlag.einzelnummer}`
  );
}

export function DsvSendung({
  sendung,
  vorschlag,
  sendungsSuche,
  onVerknuepfen,
  onLoesen,
  laeuft,
  bestellungId,
  mitgliederAnzahl,
  bestellNr,
}: DsvSendungProps) {
  /** Rueckfrage vor dem Loesen der Verknuepfung (Task 6, Sammelsendung aufloesen). */
  const [trennenOffen, setTrennenOffen] = useState(false);
  /** Weggeklickter Vorschlag (Auftrag 2b) - siehe Kopfkommentar "Zuordnungsvorschlag". */
  const [vorschlagVerworfen, setVorschlagVerworfen] = useState(false);
  useEffect(() => {
    setVorschlagVerworfen(false);
  }, [bestellungId, vorschlag?.shipmentId]);

  /**
   * `onAuswahl` von `SendungsSuche`: verknuepft immer direkt, OHNE Fremd-Check (Kopfkommentar
   * „Konfliktdialog beim Verknuepfen - ENTFERNT") - eine Sendung mit anderen Bestellungen ist seit
   * der Sammelsendung der Normalfall.
   */
  function beiAuswahl(treffer: SendungsTreffer): void {
    onVerknuepfen?.(treffer.id);
  }

  if (!sendung) {
    return (
      <div className="field-grid">
        {/* Auftrag 2b: nur, wenn ein Vorschlag geladen UND nicht weggeklickt ist - sonst sieht die
            Ansicht aus wie vor diesem Auftrag (siehe Kopfkommentar). */}
        {vorschlag && !vorschlagVerworfen && (
          <div className="field wide">
            <div className="ship-vorschlag" role="alert">
              <div className="ship-vorschlag-kopf">
                <span className="ship-vorschlag-titel">Sendung {vorschlag.dsvShipmentNo}</span>
                <span className="ship-vorschlag-details">
                  {[
                    vorschlag.shipper,
                    vorschlag.eta ? `ETA ${fmtDateNum(vorschlag.eta)}` : null,
                    vorschlag.dischargePort,
                  ]
                    .filter((teil): teil is string => !!teil)
                    .join(" · ")}
                </span>
              </div>
              <p className="ship-vorschlag-grund">
                {vorschlagGrundText(vorschlag)}
              </p>
              <div className="ship-vorschlag-aktionen">
                <button
                  type="button"
                  className="btn-primary"
                  disabled={!!laeuft}
                  onClick={() => onVerknuepfen?.(vorschlag.shipmentId)}
                >
                  Übernehmen
                </button>
                <button type="button" className="btn-ghost" onClick={() => setVorschlagVerworfen(true)}>
                  Verwerfen
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="field wide">
          {/* Ohne `sendungsSuche` bleibt das Suchfeld sichtbar, aber stumm - derselbe Umgang wie
              bei `jtlArtikelSuche` in `MainTab/Positionen.tsx`. */}
          <SendungsSuche
            suche={sendungsSuche ?? KEINE_SENDUNGSSUCHE}
            onAuswahl={beiAuswahl}
            disabled={!!laeuft}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="field-grid">
      <Anzeigefeld label="DSV-Sendungsnr." wert={sendung.dsvShipmentNo ?? ""} />
      <Anzeigefeld label="Master-BL/AWB" wert={sendung.masterBlAwb ?? ""} />
      <Anzeigefeld label="Booking-Ref." wert={sendung.bookingRef ?? ""} />
      <Anzeigefeld label="Container-Nr." wert={sendung.containerNo ?? ""} />
      <Anzeigefeld label="Shipper" wert={sendung.shipper ?? ""} />
      <Anzeigefeld label="Consignee" wert={sendung.consignee ?? ""} />
      <Anzeigefeld label="ETA" wert={fmtDateNum(sendung.eta)} />
      <Anzeigefeld label="letzter Abgleich" wert={fmtDT(sendung.lastSyncedAt)} />
      <div className="field wide">
        <button
          type="button"
          className="ship-loesen"
          disabled={!!laeuft}
          onClick={() => setTrennenOffen(true)}
        >
          <Ikone name="unlink" />
          Verknüpfung lösen
        </button>
      </div>
      {trennenOffen && (
        <TrennenDialogLazy
          art={(mitgliederAnzahl ?? 0) >= 2 ? "sammel" : "einzel"}
          dsvNo={sendung.dsvShipmentNo ?? ""}
          orderNos={bestellNr ? [bestellNr] : []}
          onAbbrechen={() => setTrennenOffen(false)}
          onBestaetigen={() => {
            setTrennenOffen(false);
            onLoesen?.(sendung.id);
          }}
        />
      )}
    </div>
  );
}
